import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from './generated/public';

export type Schemas = components['schemas'];
export type ApiPaths = paths;
export type KidovaClient = Client<paths>;

/** The §21.5 error envelope, plus two client-side codes the server never sends. */
export type ErrorCode = Schemas['ErrorBody']['code'] | 'NETWORK' | 'UNEXPECTED';

export interface ErrorDetail {
  field?: string | null;
  code: string;
  message: string;
}

/** Thrown by `unwrap`: the envelope's code and message, and the request id support needs. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly requestId: string | null;
  readonly details: readonly ErrorDetail[];
  /** Seconds from the Retry-After header of a 429 (§10.3 rate limits), when the server sent it. */
  readonly retryAfterS: number | null;

  constructor(
    status: number,
    code: ErrorCode,
    message: string,
    requestId: string | null,
    details: readonly ErrorDetail[] = [],
    retryAfterS: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
    this.retryAfterS = retryAfterS;
  }

  /** Field → message, for forms (422 VALIDATION_ERROR). */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details) if (d.field) out[d.field] ??= d.message;
    return out;
  }
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * ADR 1002: 401 STEP_UP_REQUIRED asks the signed-in parent to confirm it's them; the session is
 * fine, so it must not count as "signed out".
 */
async function needsStepUp(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as { error?: { code?: string } | null };
    return body.error?.code === 'STEP_UP_REQUIRED';
  } catch {
    return false;
  }
}

export function isStepUpRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'STEP_UP_REQUIRED';
}

export interface ClientOptions {
  /** Same-origin BFF prefix (§11.9: Traefik routes app.kidova.com/api/* to api-public). */
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  /** The CSRF token from `/v1/web/session` or the login response; sent on every mutation. */
  csrfToken?: () => string | null;
  /** Called on 401 so the app can drop its session and show the sign-in page. */
  onUnauthorized?: (path: string, method: string) => void;
}

export function createApiClient(options: ClientOptions = {}): KidovaClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? '/api',
    credentials: 'same-origin',
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  const middleware: Middleware = {
    onRequest({ request }) {
      if (MUTATING.has(request.method)) {
        const token = options.csrfToken?.();
        if (token) request.headers.set('X-CSRF-Token', token);
      }
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status === 401 && !(await needsStepUp(response)))
        options.onUnauthorized?.(new URL(request.url, 'http://x').pathname, request.method);
      return response;
    },
  };
  client.use(middleware);
  return client;
}

/** One key per user action; a retry of that action reuses it so the server replays (§21.9). */
export function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

interface EnvelopeLike<T> {
  data?: T | null;
  meta?: { request_id?: string; next_cursor?: string | null };
  error?: { code: ErrorCode; message: string; details?: ErrorDetail[] } | null;
}

interface FetchResult<T> {
  data?: EnvelopeLike<T>;
  error?: unknown;
  response: Response;
}

type DataOf<R> = R extends { data?: infer E }
  ? E extends EnvelopeLike<infer T>
    ? T
    : never
  : never;

/** Retry-After in seconds (RFC 9110 §10.2.3); the API sends the delay form, never a date. */
export function retryAfterSeconds(value: string | null): number | null {
  return value && /^\s*\d+\s*$/.test(value) ? Number(value) : null;
}

function toError(result: FetchResult<unknown>): ApiError {
  const body = (result.error ?? result.data) as EnvelopeLike<unknown> | undefined;
  const requestId = body?.meta?.request_id ?? result.response.headers.get('x-request-id');
  const retryAfter = retryAfterSeconds(result.response.headers.get('retry-after'));
  if (body?.error) {
    return new ApiError(
      result.response.status,
      body.error.code,
      body.error.message,
      requestId,
      body.error.details ?? [],
      retryAfter,
    );
  }
  return new ApiError(
    result.response.status,
    'UNEXPECTED',
    `Unexpected response (${String(result.response.status)})`,
    requestId,
    [],
    retryAfter,
  );
}

async function settle<R extends FetchResult<unknown>>(call: Promise<R>): Promise<R> {
  try {
    return await call;
  } catch (cause) {
    const error = new ApiError(0, 'NETWORK', 'Network error', null);
    error.cause = cause;
    throw error;
  }
}

/** The envelope's `data`, or an ApiError with the envelope's code, message and request id. */
export async function unwrap<R extends FetchResult<unknown>>(call: Promise<R>): Promise<DataOf<R>> {
  const result = await settle(call);
  if (!result.response.ok || result.data?.error) throw toError(result);
  return result.data?.data as DataOf<R>;
}

export interface Page<T> {
  items: T;
  nextCursor: string | null;
}

/** Cursor pages (§10.3 `?limit=&cursor=`): the items and `meta.next_cursor`. */
export async function unwrapPage<R extends FetchResult<unknown>>(
  call: Promise<R>,
): Promise<Page<DataOf<R>>> {
  const result = await settle(call);
  if (!result.response.ok || result.data?.error) throw toError(result);
  return {
    items: result.data?.data as DataOf<R>,
    nextCursor: result.data?.meta?.next_cursor ?? null,
  };
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
